import React, { useState, useEffect } from 'react';
import { useAndroidBackNavigation } from '../hooks/use-android-back-navigation';
import { useBackNavigation } from '../hooks/use-back-navigation';
import { useNavigate } from 'react-router-dom';
import { AppBar } from './ui/app-bar';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';

const BackButtonTest = () => {
  const navigate = useNavigate();
  const [testInput, setTestInput] = useState('');
  const [navigationCount, setNavigationCount] = useState(0);
  const [testResults, setTestResults] = useState<string[]>([]);
  
  // Use the main back navigation hook for comprehensive testing
  const { handleBack, isAndroid, isIOS, isMobile, isOnDashboard } = useBackNavigation({
    onBack: () => {
      setNavigationCount(prev => prev + 1);
      navigate(-1);
    }
  });

  // Detect platform details
  const isNative = !!((window as any).Capacitor && typeof (window as any).Capacitor.isNativePlatform === 'function' && (window as any).Capacitor.isNativePlatform());
  const isAndroidWeb = isAndroid && !isNative;

  // Add test results
  const addTestResult = (result: string) => {
    setTestResults(prev => [...prev, `${new Date().toLocaleTimeString()}: ${result}`]);
  };

  // Test back button manually
  const testBackButton = () => {
    console.log('Testing back button manually...');
    addTestResult('Manual back button test triggered');
    handleBack();
  };

  // Test global handler
  const testGlobalHandler = () => {
    console.log('Testing global back navigation handler...');
    addTestResult('Global handler test triggered');
    if (window.handleBackNavigation) {
      window.handleBackNavigation();
    } else {
      addTestResult('Global handler not found');
    }
  };

  // Test IME clear button
  const testIMEClear = () => {
    addTestResult('IME clear button test - type text and press X button');
  };

  // Check for syntax errors on load
  useEffect(() => {
    addTestResult('Component loaded successfully - no syntax errors');
    addTestResult(`Platform: Android=${isAndroid}, iOS=${isIOS}, Mobile=${isMobile}`);
    addTestResult(`Native: ${isNative}, Android Web: ${isAndroidWeb}`);
    addTestResult(`On Dashboard: ${isOnDashboard}`);
    addTestResult(`History Length: ${window.history.length}`);
    
    // Check if there are any console errors
    const originalError = console.error;
    console.error = (...args) => {
      addTestResult(`ERROR: ${args.join(' ')}`);
      originalError.apply(console, args);
    };

    return () => {
      console.error = originalError;
    };
  }, [isAndroid, isIOS, isMobile, isNative, isAndroidWeb, isOnDashboard]);

  return (
    <>
      {/* Mobile App Bar */}
      <div className="md:hidden">
        <AppBar
          title="Back Button Test"
          onBack={() => navigate(-1)}
          showBackButton={true}
        />
      </div>

      <div className="container mx-auto p-4 mt-14 md:mt-0">
        <Card className="max-w-md mx-auto">
          <CardHeader>
            <CardTitle>Back Button Test - Syntax Error Fixed</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <p><strong>Platform Detection:</strong></p>
              <ul className="text-sm space-y-1">
                <li>Android: {isAndroid ? '✅ Yes' : '❌ No'}</li>
                <li>iOS: {isIOS ? '✅ Yes' : '❌ No'}</li>
                <li>Mobile: {isMobile ? '✅ Yes' : '❌ No'}</li>
                <li>Native: {isNative ? '✅ Yes' : '❌ No'}</li>
                <li>Android Web: {isAndroidWeb ? '✅ Yes' : '❌ No'}</li>
                <li>On Dashboard: {isOnDashboard ? '✅ Yes' : '❌ No'}</li>
                <li>Navigation Count: {navigationCount}</li>
                <li>History Length: {window.history.length}</li>
              </ul>
            </div>

            <div className="space-y-2">
              <p><strong>Test Buttons:</strong></p>
              <div className="space-y-2">
                <Button 
                  onClick={testBackButton}
                  className="w-full"
                  variant="outline"
                >
                  Test Back Navigation
                </Button>
                
                <Button 
                  onClick={testGlobalHandler}
                  className="w-full"
                  variant="outline"
                >
                  Test Global Handler
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <p><strong>IME Clear Button Test:</strong></p>
              <div className="space-y-2">
                <Label htmlFor="test-input">Test Input Field</Label>
                <Input
                  id="test-input"
                  type="text"
                  value={testInput}
                  onChange={(e) => setTestInput(e.target.value)}
                  onFocus={testIMEClear}
                  placeholder="Type here, then press IME clear (X) button..."
                  className="w-full"
                />
                <p className="text-sm text-gray-600">
                  Current value: "{testInput}" | Navigation count: {navigationCount}
                </p>
                <p className="text-xs text-gray-500">
                  • Type text in the input field above
                  • Press the IME clear (X) button on your keyboard
                  • Verify that only the text clears, no navigation occurs
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <p><strong>Hardware Back Button Test:</strong></p>
              <ul className="text-sm space-y-1">
                <li>• Use hardware back button (Android)</li>
                <li>• Use back gesture (Android)</li>
                <li>• Click the back button in AppBar</li>
                <li>• Use browser back button (Web)</li>
                {isAndroidWeb && (
                  <>
                    <li className="text-blue-600">• Android Web: Should show exit confirmation on dashboard</li>
                    <li className="text-blue-600">• Android Web: Should navigate back on sub-pages</li>
                    <li className="text-blue-600">• Android Web: Should not close site unexpectedly</li>
                  </>
                )}
              </ul>
            </div>

            <div className="space-y-2">
              <p><strong>Test Results:</strong></p>
              <div className="max-h-32 overflow-y-auto bg-gray-50 p-2 rounded text-xs">
                {testResults.length === 0 ? (
                  <p className="text-gray-500">No test results yet...</p>
                ) : (
                  testResults.map((result, index) => (
                    <div key={index} className="mb-1">
                      {result}
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="space-y-2">
              <p><strong>Status:</strong></p>
              <div className="text-sm">
                <p className="text-green-600">✅ Syntax Error Fixed</p>
                <p className="text-green-600">✅ Unminified Build</p>
                <p className="text-green-600">✅ Relative Asset Paths</p>
                <p className="text-green-600">✅ Error Handling Added</p>
                <p className="text-green-600">✅ Mobile Web Back Button Fix</p>
                <p className="text-green-600">✅ Exit Confirmation Dialog</p>
                {isAndroidWeb && (
                  <p className="text-blue-600">🔵 Android Web Mode Active</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default BackButtonTest;
